import { UPLOAD_SOURCES, getLogger } from '@cld/upload-widget-common';
import createReducer from 'common/reducerBase';
import { isMobile, isSafari } from 'utils/domUtils';
import { isSupported as isCameraMediaSupported } from '../../utils/camera';
import { types } from '../actions';
import WidgetConfigRecord, { takeUiParams } from '../data/WidgetConfigRecord';

const logger = getLogger();

const isSecureValidator = (config, secure) => ({
  valid: !!secure,
  reason: "'secure' option must be turned on",
});

const isNotSafariValidator = () => ({
  valid: !isSafari(),
  reason: 'source is not supported on the Safari browser',
});

const isCameraAvailable = (config, secure) => {
  const loc = self.location;
  const validations = [
    isSecureValidator(config, secure),
    {
      valid: (loc.hostname === 'localhost' || loc.protocol === 'https:') && isCameraMediaSupported() && !isMobile(),
      reason: 'only supported on https and desktop browsers',
    },
  ];

  const firstInvalid = validations.find((v) => !v.valid);

  return firstInvalid || { valid: true };
};

const isGDriveAvailable = (config, secure) => {
  const isSecure = isSecureValidator(config, secure);
  return isSecure.valid ? isNotSafariValidator() : isSecure;
};

const sourcesValidators = {
  [UPLOAD_SOURCES.INSTAGRAM]: isSecureValidator,
  [UPLOAD_SOURCES.DROPBOX]: isSecureValidator,
  [UPLOAD_SOURCES.GOOGLE_DRIVE]: isGDriveAvailable,
  [UPLOAD_SOURCES.IMAGE_SEARCH]: (config) => ({
    valid: !!config.googleApiKey,
    reason: 'Google API Key must be provided',
  }),
  [UPLOAD_SOURCES.CAMERA]: isCameraAvailable,
  [UPLOAD_SOURCES.SHUTTERSTOCK]: isSecureValidator,
  [UPLOAD_SOURCES.GETTY]: isSecureValidator,
  [UPLOAD_SOURCES.ISTOCK]: isSecureValidator,
  [UPLOAD_SOURCES.UNSPLASH]: isSecureValidator,
};

/**
 * remove duplicates from sources config and validate each source
 * @param config
 * @param secure
 * @returns {Array<sources>} validated sources list
 */
const getValidSources = (config, secure) => {
  const validSources = [].concat(
    // using concat here to make sure we always use a plain array and not an immutable object
    config.sources
      ? [...new Set(config.sources)].filter((source) => {
          const result = !sourcesValidators[source] ? { valid: true } : sourcesValidators[source](config, secure);

          if (!result.valid) {
            logger.warn(`[widgetConfigReducer]: removing ${source} - ${result.reason}`);
          }

          return result.valid;
        })
      : []
  );

  if (!validSources.length) {
    logger.warn('[widgetConfigReducer]: no valid sources configured, defaulting to local');
    validSources.push(UPLOAD_SOURCES.LOCAL);
  }

  return validSources;
};

const getValidDefaultSource = (defaultSrc, sources) => {
  if (!defaultSrc || !~sources.indexOf(defaultSrc)) {
    logger.log(`[widgetConfigReducer]: default source ${defaultSrc || 'null'} is not present in valid&enabled sources`);
    defaultSrc = sources[0]; // use the first one as the default
  }

  return defaultSrc;
};

const getIsShowAdvancedOptions = (payload) => payload.showAdvancedOptions && !payload.tags && !payload.publicId; // cant have advanced options with either tags or public id provided

const getValidConfig = (state, payload) => {
  const uiConfig = takeUiParams(payload);
  const validSources = uiConfig.sources && uiConfig.sources && getValidSources(uiConfig, payload.secure);
  const searchBySites = uiConfig.searchBySites && uiConfig.searchBySites.length ? uiConfig.searchBySites : ['all'];

  return {
    ...uiConfig,
    showAdvancedOptions: getIsShowAdvancedOptions(payload),
    searchBySites,
    sources: validSources || state.sources,
    defaultSource: validSources ? getValidDefaultSource(payload.defaultSource, validSources) : state.defaultSource,
  };
};

export default createReducer(WidgetConfigRecord(), {
  [types.SET_WIDGET_CONFIG]: (state, { payload }) => state.merge(getValidConfig(state, payload)),
});
